import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FileType } from '@app/enums/FileTypes';
import { CurrencyExchangeRateService } from '@app/v2/services/currency-exchange-rate/currency-exchange-rate.service';
import { AttachmentService } from '@app/v2/services/travel-request/attachment.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { MedpaceDatepickerComponent } from '@components/atoms/medpace-datepicker/medpace-datepicker.component';
import { MedpaceUploadComponent } from '@components/atoms/medpace-upload/medpace-upload.component';
import {
    Chip,
    MdsCardComponent,
    MdsCardContentComponent,
    MdsChipComponent,
    MdsDropdownComponent,
    MdsIconButtonComponent,
    MdsTextareaComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { CurrencyViewModel } from '@models/currency';
import { Receipt, TravelRequestStatus } from '@models/request';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { TravelRequestStateService } from '@services/state-management/travel-request-state.service';
import { TravelViewModelService } from '@services/travel-view-model/travel-view-model.service';
import {
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    filter,
    map,
    merge,
    of,
    shareReplay,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { MerchantDetailsEditComponent } from '../../merchant-details/merchant-details-edit/merchant-details-edit.component';
import { ReceiptFormGroup } from './receipt-edit.formgroup';

@Component({
    selector: 'medpace-receipt-edit',
    imports: [
        MedpaceDatepickerComponent,
        MdsTextFieldComponent,
        MdsDropdownComponent,
        AsyncPipe,
        MedpaceUploadComponent,

        MerchantDetailsEditComponent,
        MdsCardComponent,
        MdsCardContentComponent,
        MdsChipComponent,
        MdsIconButtonComponent,
        MdsTextareaComponent,
    ],
    templateUrl: './receipt-edit.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class ReceiptEditComponent {
    index = input.required<number>();
    formGrp = input.required<ReceiptFormGroup>();
    formGrp$ = toObservable(this.formGrp);
    model = input<Receipt | null>();
    model$ = toObservable(this.model);
    isEditMode = input.required<boolean>();
    isEditMode$ = toObservable(this.isEditMode);
    downloadAttachmentEvent = output<{ attachmentId: number; attachmentName: string }>();
    removeEvent = output<ReceiptFormGroup>();
    travelRequestStateService = inject(TravelRequestStateService);
    private readonly attachmentService = inject(AttachmentService);
    private readonly userService = inject(UserServiceV2);
    patientStateService = inject(PatientStateService);
    currencyStateService = inject(CurrencyStateService);
    isCrc = UserServiceV2.isCrc();
    currencyOptions$ = this.currencyStateService.getCurrenciesAsMdsOptions();

    private readonly currencyExchangeRateService = inject(CurrencyExchangeRateService);

    currency$ = this.formGrp$.pipe(
        switchMap((formGrp) => formGrp.controls.currency.valueChangesReplay),
        shareReplay(1)
    );
    isCurrencyUSD$ = this.currency$.pipe(
        filter(Boolean),
        map((currency) => currency.isocode === 'USD')
    );

    private readonly chipAmounts$ = combineLatest({
        model: this.model$,
        formGrp: this.formGrp$,
    }).pipe(
        switchMap(({ model, formGrp }) => {
            const initial$ = of({
                usd: model?.amountUsd ?? null,
                exchangeRateDate: model?.exchangeRateDate ?? null,
            });

            const calculate$ = combineLatest({
                amount: formGrp.controls.amountInLocalCurrency.valueChangesReplay,
                date: formGrp.controls.receiptDate.valueChangesReplay,
                currency: this.currency$,
            }).pipe(
                debounceTime(300),
                switchMap(({ amount, date, currency }) => {
                    if (!amount || !date || !currency || currency.isocode.toUpperCase() === 'USD') {
                        return of({ usd: null as number | null, exchangeRateDate: null as string | null });
                    }
                    return this.currencyExchangeRateService
                        .convertAmount(amount, currency.isocode.toUpperCase(), date, null)
                        .pipe(map((res) => ({ usd: res.usd, exchangeRateDate: res.exchangeRateDate })));
                })
            );

            // outside edit mode keep the last emitted amounts instead of resetting them
            const recalculate$ = this.isEditMode$.pipe(
                distinctUntilChanged(),
                switchMap((isEditMode) => (isEditMode ? calculate$ : EMPTY))
            );

            return merge(initial$, recalculate$);
        })
    );

    private readonly chipAmountsSignal = toSignal(this.chipAmounts$);
    private readonly currencySignal = toSignal(this.currency$);

    amountUsdChip = computed((): Chip[] | null => {
        const currency = this.currencySignal();
        if (!currency || currency.isocode.toUpperCase() === 'USD') return null;
        const amounts = this.chipAmountsSignal();
        if (amounts?.usd == null) return null;
        const value = `${amounts.usd.toFixed(2)} USD · reporting`;
        return [{ value, ariaLabel: value, removable: false }];
    });
    elevatedAccess$ = this.userService.permissions.request.access.elevated$;
    isInProcessOrHigher$ = this.travelRequestStateService.getStaticState().pipe(
        map((request) => !!request && request.status?.statusId > TravelRequestStatus.Submitted),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    readonly acceptableFileTypes = [FileType.PDF, FileType.JPG, FileType.JPEG, FileType.PNG];

    travelViewModelService = inject(TravelViewModelService);
    // returns the receiptTypeOption associated with current receipt type saved in db
    initialReceiptTypeOptions$ = this.model$.pipe(
        map((receipt) => {
            if (!receipt) return []; // create new receipt or not yet populated

            const initialOption = this.travelRequestStateService.receiptTypeOptions.find(
                (option) => option.value === receipt.type
            );
            if (!initialOption) throw new Error('Missing option');
            return [initialOption];
        })
    );
    // will continuosly emit receiptTypeOptions associated with travel request tabs that have the 'Needs travel' control set to true
    // e.g. if Flight and Other have been switched to true by the user, it will emit [this.travelRequestStateService.receiptTypeOption_Flight ,this.travelRequestStateService.receiptTypeOption_Other]
    // if then the user deselects Other tab, [this.travelRequestStateService.receiptTypeOption_Flight] will be emitted
    dynamicReceiptTypeOptions$ = toObservable(this.travelViewModelService.travelFormGroup).pipe(
        filter(Boolean), // formGroup can be undefined in the beginning
        switchMap((formGroup) => {
            return combineLatest({
                // needsTravel formControl for every tab
                flight: formGroup.controls.travelDetails.controls.flight.controls.needsTravel.valueChanges.pipe(
                    startWith(formGroup.controls.travelDetails.controls.flight.controls.needsTravel.value),
                    map((needsTravel) => (needsTravel ? this.travelRequestStateService.receiptTypeOption_Flight : null)) // if this tab is desected, emit null, it will later get filtered out
                ),
                train: formGroup.controls.travelDetails.controls.train.controls.needsTravel.valueChanges.pipe(
                    startWith(formGroup.controls.travelDetails.controls.train.controls.needsTravel.value),
                    map((needsTravel) => (needsTravel ? this.travelRequestStateService.receiptTypeOption_Train : null))
                ),
                lodging: formGroup.controls.travelDetails.controls.lodging.controls.needsTravel.valueChanges.pipe(
                    startWith(formGroup.controls.travelDetails.controls.lodging.controls.needsTravel.value),
                    map((needsTravel) =>
                        needsTravel ? this.travelRequestStateService.receiptTypeOption_Lodging : null
                    )
                ),
                rentalCar: formGroup.controls.travelDetails.controls.rentalCar.controls.needsTravel.valueChanges.pipe(
                    startWith(formGroup.controls.travelDetails.controls.rentalCar.controls.needsTravel.value),
                    map((needsTravel) =>
                        needsTravel ? this.travelRequestStateService.receiptTypeOption_RentalCar : null
                    )
                ),
                carService: formGroup.controls.travelDetails.controls.carService.controls.needsTravel.valueChanges.pipe(
                    startWith(formGroup.controls.travelDetails.controls.carService.controls.needsTravel.value),
                    map((needsTravel) =>
                        needsTravel ? this.travelRequestStateService.receiptTypeOption_CarService : null
                    )
                ),
                other: formGroup.controls.travelDetails.controls.other.controls.needsTravel.valueChanges.pipe(
                    startWith(formGroup.controls.travelDetails.controls.other.controls.needsTravel.value),
                    map((needsTravel) => (needsTravel ? this.travelRequestStateService.receiptTypeOption_Other : null))
                ),
            }).pipe(
                map((receiptTypeOptions) => {
                    return Object.values(receiptTypeOptions).filter((option) => option !== null);
                })
            );
        }),
        shareReplay(1)
    );
    // combines the initial receipt options (what's saved in the db and thus shouldn't be disallowed)
    // with dynamic options that will change as the user selects/deselects travel request tabs
    receiptTypeOptions$ = combineLatest({
        initialReceiptTypeOptions: this.initialReceiptTypeOptions$,
        dynamicReceiptTypeOptions: this.dynamicReceiptTypeOptions$,
    }).pipe(
        map(({ initialReceiptTypeOptions, dynamicReceiptTypeOptions }) => {
            const asSet = new Set([...initialReceiptTypeOptions, ...dynamicReceiptTypeOptions]); // removes duplicates
            return [...asSet]; // converts set to array
        }),
        shareReplay(1)
    );

    push(request: { travelRequestId?: number; reimbursementRequestId?: number; stipendRequestId?: number }): Receipt {
        const value = this.formGrp().getRawValue();
        let model = this.model();
        if (!model)
            model = {
                id: 0,
            } as Receipt;

        model.amountLocalCurrency = value.amountInLocalCurrency;
        model.amountUsd = this.chipAmountsSignal()?.usd ?? null;
        model.exchangeRateDate = this.chipAmountsSignal()?.exchangeRateDate ?? null;
        model.doc = value.file;

        const fileControl = this.formGrp().controls.file;
        if (fileControl.dirty) {
            // user modified file
            if (!fileControl.value) {
                // user removed file
                model.attachmentInfoId = null;
            } else {
                // user replaced file
                model.attachmentInfoId = 0;
            }
        } else {
            // leave attachmentId unchanged
        }
        model.currencyCode = value.currency?.isocode ?? null;
        model.merchant = value.merchant.merchant;
        model.receiptDate = value.receiptDate;
        model.reimbursementRequestId =
            request.reimbursementRequestId !== undefined ? request.reimbursementRequestId : null;
        model.travelRequestId = request.travelRequestId !== undefined ? request.travelRequestId : null;
        model.stipendRequestId = request.stipendRequestId !== undefined ? request.stipendRequestId : null;
        model.transactionDate = value.merchant.transactionDate;
        model.type = value.type!; // there's a Validators.required, so won't be null
        model.erTrackingId = value.erTrackingId;
        model.erReportingPeriod = value.erReportingPeriod;
        model.notes = value.notes;

        return model;
    }

    patch$ = combineLatest({
        formGrp: this.formGrp$,
        model: this.model$,
        currencies: this.currencyStateService.getCurrencies(),
    })
        .pipe(
            filter(({ model }) => !!model),
            tap(({ model, formGrp, currencies }) => {
                formGrp.patchValue({
                    type: model!.type,
                    amountInLocalCurrency: model!.amountLocalCurrency,
                    amountInUsd: model!.amountUsd,
                    currency: currencies.find((currency) => currency.isocode === model!.currencyCode) ?? null,
                    receiptDate: model!.receiptDate,
                    merchant: {
                        merchant: model!.merchant,
                        transactionDate: model!.transactionDate,
                    },
                    file: null,
                    erTrackingId: model!.erTrackingId,
                    erReportingPeriod: model!.erReportingPeriod,
                    notes: model!.notes ?? null,
                });
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    initializePreferredCurrencyEffect$ = combineLatest({
        model: this.model$,
        patient: this.patientStateService.getPatient(),
        currencies: this.currencyStateService.getCurrencies(),
        currencyUSD: this.currencyStateService.getCurrencyUSD(),
        formGrp: this.formGrp$,
    })
        .pipe(
            filter(({ model }) => !model), // no model, new receipt being created
            tap(({ currencies, currencyUSD, patient, formGrp }) => {
                let currency: CurrencyViewModel | undefined = undefined;
                if (patient?.preferredCurrency) {
                    currency = currencies.find((currency) => currency.isocode === patient.preferredCurrency);
                }
                if (!currency) currency = currencyUSD;

                if (currency) formGrp.controls.currency.setValue(currency);
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    protected downloadAttachment(): () => void {
        return () => {
            const model = this.model();
            if (!model) throw new Error('Receipt model is missing!');
            if (!model.attachmentInfoId) throw new Error('Missing attachment id!');

            const requestId = model.travelRequestId; // for now only supports travelRequst, attachmentService also only downloads from travelrequest
            if (!requestId) throw new Error('Travel request id missing!');

            if (!model.type) throw new Error('Receipt type missing!');
            const attachmentName = this.attachmentService.travelRequestAttachmentName;

            this.attachmentService.downloadAttachment(requestId, {
                attachmentId: model.attachmentInfoId,
                attachmentName: attachmentName,
            });
        };
    }

    protected onFileSelected(file: File) {
        this.formGrp().controls.file.setValue(file);
        this.formGrp().controls.file.markAsDirty(); // programmatic changes do not mark control as dirty
    }
    removeReceipt() {
        this.removeEvent.emit(this.formGrp());
    }
}
